import React from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";

const TESTIMONIALS = [
  {
    name: "Camila R.",
    role: "Mamá de Luca (3) y Sofía (6)",
    pain: "El estrés diario me ganaba",
    text: "Antes lloraba de cansancio pensando qué cocinar. Ahora abro el ebook el domingo y mi semana queda lista. Mis hijos comen mejor y yo descanso por primera vez en años.",
    photo: "/img/avatar-1.webp",
  },
  {
    name: "Andrea M.",
    role: "Mamá de Tomás (1)",
    pain: "Tenía miedo de la alimentación complementaria",
    text: "Las recetas por edad fueron clave. Sentí confianza por primera vez al introducir alimentos sólidos. Es súper claro, cariñoso y se nota que está hecho con amor.",
    photo: "/img/avatar-2.webp",
  },
  {
    name: "Valeria S.",
    role: "Mamá de Mateo (8)",
    pain: "Mi hijo era extremadamente selectivo",
    text: "Mateo rechazaba todo lo verde. Probamos las meriendas y los desayunos del ebook y poco a poco volvió a disfrutar la comida. Hoy hasta me pide los bowls de quinoa.",
    photo: "/img/avatar-3.webp",
  },
  {
    name: "Paula G.",
    role: "Mamá de Emma (4) y Joaquín (11)",
    pain: "No tenía ideas, repetía lo mismo",
    text: "Las listas de compras me ahorran horas y la planificación semanal me regresó la cabeza. Cocino menos veces, pero mucho mejor. Mis hijos están encantados.",
    photo: "/img/avatar-4.webp",
  },
  {
    name: "Daniela T.",
    role: "Mamá de Renata (5)",
    pain: "No sabía si lo hacía bien",
    text: "Es como tener una nutricionista pediátrica en casa, pero sin culpas. Recetas reales, ingredientes simples y mucho cariño. Lo recomiendo a todas mis amigas.",
    photo: "/img/avatar-5.webp",
  },
  {
    name: "Lucía F.",
    role: "Mamá de Bruno (13)",
    pain: "Mi hijo adolescente comía solo pasta",
    text: "Bruno entró a la adolescencia con hambre infinita. Las recetas 12-15 son nutritivas, abundantes y le encantan. Ya no peleamos en la mesa, comemos juntos.",
    photo: "/img/avatar-6.webp",
  },
];

export default function Testimonials() {
  return (
    <section
      id="testimonios"
      data-testid="testimonials-section"
      className="bg-[#ECE0CC] py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#849983]">
            Mamás que ya lo viven
          </p>
          <h2 className="mt-4 font-serif-display text-4xl md:text-5xl tracking-tight leading-tight text-[#2A302A]">
            Transformaciones reales en <em className="italic">cocinas reales</em>.
          </h2>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
            <p className="text-[15px] text-[#2A302A]">
              Más de <strong>2,400 mamás</strong> ya utilizan{" "}
              <strong>Pequeños Platos</strong>
            </p>
            <div className="flex items-center gap-1.5 text-[#D4A574]">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} className="h-4 w-4 fill-current" strokeWidth={0} />
              ))}
              <span className="ml-1 text-sm font-semibold text-[#2A302A]">4.9/5</span>
              <span className="text-xs text-[#576057] ml-1">valoración promedio</span>
            </div>
          </div>
          <p className="mt-5 text-[#576057] leading-relaxed">
            Cientos de mamás dejaron atrás el estrés, los niños selectivos y la rutina
            repetitiva. Estas son sus historias.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              whileHover={{ y: -4 }}
              data-testid={`testimonial-card-${i}`}
              className="bg-white rounded-3xl p-8 border border-[#E6E2D6] shadow-[0_12px_36px_rgba(42,48,42,0.05)] hover:shadow-[0_24px_60px_rgba(42,48,42,0.1)] transition-all duration-500 flex flex-col"
            >
              <div className="flex items-center gap-1 text-[#C8A892]">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="h-4 w-4 fill-current" strokeWidth={0} />
                ))}
              </div>
              <Quote className="h-6 w-6 text-[#849983]/30 mt-4" />
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#C8A892] mt-3">
                «{t.pain}»
              </p>
              <p className="text-[15px] leading-relaxed text-[#2A302A] mt-2 flex-1">
                {t.text}
              </p>
              <div className="mt-6 flex items-center gap-3 pt-5 border-t border-[#E6E2D6]">
                <img loading="lazy" decoding="async"
                  src={t.photo}
                  alt={t.name}
                  className="w-12 h-12 rounded-full object-cover border border-[#E6E2D6]"
                />
                <div>
                  <div className="font-medium text-[#2A302A] text-sm">{t.name}</div>
                  <div className="text-xs text-[#8A948A]">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
